import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'customer' | 'retailer' | 'distributor' | 'admin';
  userType?: 'retailer' | 'distributor';
  companyName?: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  tier: 'bronze' | 'silver' | 'gold' | 'platinum';
  loyaltyPoints: number;
  isEmailVerified: boolean;
  preferences: {
    newsletter: boolean;
    notifications: boolean;
  };
  lastLogin?: string;
  createdAt: string;
}

export interface CartItem {
  product: {
    _id: string;
    name: string;
    brand: string;
    images: Array<{ url: string; alt?: string; isPrimary?: boolean }>;
    pricing: {
      retail: { price: number; compareAtPrice?: number };
      retailer: { price: number; minQuantity: number };
      distributor: { price: number; minQuantity: number };
    };
  };
  quantity: number;
  price: number;
  originalPrice: number;
  tier: 'retail' | 'retailer' | 'distributor';
  discount: number;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

interface CartState {
  items: CartItem[];
  totalItems: number;
  totalAmount: number;
}

interface AppState {
  // Auth state
  auth: AuthState;
  
  // Cart state
  cart: CartState;
  
  // Actions
  setAuth: (user: User | null, token: string | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  
  // Cart actions
  addToCart: (item: CartItem) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  
  // Utility actions
  getCartItemCount: () => number;
  getCartTotal: () => number;
}

export const useStore = create<AppState>()(
  persist(
    (set, get) => ({
      // Initial auth state
      auth: {
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      },
      
      // Initial cart state
      cart: {
        items: [],
        totalItems: 0,
        totalAmount: 0,
      },
      
      // Auth actions
      setAuth: (user, token) => {
        set((state) => ({
          auth: {
            ...state.auth,
            user,
            token,
            isAuthenticated: !!user && !!token,
            error: null,
          },
        }));
        
        if (token) {
          localStorage.setItem('token', token);
        }
      },
      
      setLoading: (loading) => {
        set((state) => ({
          auth: {
            ...state.auth,
            isLoading: loading,
          },
        }));
      },
      
      setError: (error) => {
        set((state) => ({
          auth: {
            ...state.auth,
            error,
          },
        }));
      },
      
      logout: () => {
        set((state) => ({
          auth: {
            ...state.auth,
            user: null,
            token: null,
            isAuthenticated: false,
            error: null,
          },
          cart: {
            items: [],
            totalItems: 0,
            totalAmount: 0,
          },
        }));
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      },
      
      // Cart actions
      addToCart: (item) => {
        set((state) => {
          const existingItem = state.cart.items.find(
            (cartItem) => cartItem.product._id === item.product._id
          );
          
          let newItems;
          if (existingItem) {
            newItems = state.cart.items.map((cartItem) =>
              cartItem.product._id === item.product._id
                ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
                : cartItem
            );
          } else {
            newItems = [...state.cart.items, item];
          }
          
          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          return {
            cart: {
              items: newItems,
              totalItems,
              totalAmount,
            },
          };
        });
      },
      
      removeFromCart: (productId) => {
        set((state) => {
          const newItems = state.cart.items.filter(
            (item) => item.product._id !== productId
          );
          
          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          return {
            cart: {
              items: newItems,
              totalItems,
              totalAmount,
            },
          };
        });
      },
      
      updateQuantity: (productId, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(productId);
          return;
        }
        
        set((state) => {
          const newItems = state.cart.items.map((item) =>
            item.product._id === productId ? { ...item, quantity } : item
          );
          
          const totalItems = newItems.reduce((sum, item) => sum + item.quantity, 0);
          const totalAmount = newItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
          
          return {
            cart: {
              items: newItems,
              totalItems,
              totalAmount,
            },
          };
        });
      },
      
      clearCart: () => {
        set({
          cart: {
            items: [],
            totalItems: 0,
            totalAmount: 0,
          },
        });
      },
      
      // Utility functions
      getCartItemCount: () => {
        return get().cart.totalItems;
      },
      
      getCartTotal: () => {
        return get().cart.totalAmount;
      },
    }),
    {
      name: 'ecommerce-store',
      partialize: (state) => ({
        auth: {
          user: state.auth.user,
          token: state.auth.token,
          isAuthenticated: state.auth.isAuthenticated,
        },
        cart: state.cart,
      }),
    }
  )
);
